(function () {
  'use strict';

  var LINK_SELECTOR = 'a.linked-colour-swatch__link[href]';
  var CONTENT_SELECTOR = '#content > .container';
  var PRODUCT_SECTION_SELECTOR = '[data-linked-product-section]';
  var PAGE_CACHE_LIMIT = 8;
  var pageCache = new Map();
  var activeController = null;
  var navigationSequence = 0;

  function getProductUrl(value) {
    try {
      var url = new URL(value, window.location.href);
      if (url.origin !== window.location.origin || url.pathname.indexOf('/products/') === -1) {
        return null;
      }
      return url;
    } catch (error) {
      return null;
    }
  }

  function getCacheKey(url) {
    var cacheUrl = new URL(url.href);
    cacheUrl.hash = '';
    return cacheUrl.href;
  }

  function trimPageCache() {
    while (pageCache.size > PAGE_CACHE_LIMIT) {
      pageCache.delete(pageCache.keys().next().value);
    }
  }

  function imagePreloadSource(image) {
    var src = image.getAttribute('src') || image.getAttribute('data-src');
    var srcset = image.getAttribute('srcset') || image.getAttribute('data-srcset');

    if (!src && srcset) {
      var lastCandidate = srcset.split(',').pop().trim().split(/\s+/)[0];
      src = lastCandidate;
    }

    return {
      src: src,
      srcset: srcset,
      sizes: image.getAttribute('sizes') || '50vw'
    };
  }

  function preloadPrimaryImage(pageDocument) {
    var image = pageDocument.querySelector(
      PRODUCT_SECTION_SELECTOR + ' .product-slideshow .slide--custom-initial img, ' +
      PRODUCT_SECTION_SELECTOR + ' .product-slideshow img'
    );

    if (!image) {
      return Promise.resolve();
    }

    var source = imagePreloadSource(image);
    if (!source.src && !source.srcset) {
      return Promise.resolve();
    }

    return new Promise(function (resolve) {
      var preloader = new Image();
      var finished = false;
      var timeoutId = window.setTimeout(finish, 700);

      function finish() {
        if (finished) return;
        finished = true;
        window.clearTimeout(timeoutId);
        resolve();
      }

      preloader.onload = finish;
      preloader.onerror = finish;
      if (source.srcset) preloader.srcset = source.srcset;
      if (source.sizes) preloader.sizes = source.sizes;
      if (source.src) preloader.src = source.src;
    });
  }

  function fetchProductDocument(url, signal) {
    var cacheKey = getCacheKey(url);
    if (pageCache.has(cacheKey)) {
      return pageCache.get(cacheKey);
    }

    var request = fetch(cacheKey, {
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      },
      signal: signal
    }).then(function (response) {
      if (!response.ok) {
        throw new Error('Product page request failed with status ' + response.status);
      }
      return response.text();
    }).then(function (html) {
      var pageDocument = new DOMParser().parseFromString(html, 'text/html');
      if (!pageDocument.querySelector(CONTENT_SELECTOR) || !pageDocument.querySelector(PRODUCT_SECTION_SELECTOR)) {
        throw new Error('The requested page did not contain a product section');
      }
      return preloadPrimaryImage(pageDocument).then(function () {
        return pageDocument;
      });
    });

    pageCache.set(cacheKey, request);
    trimPageCache();

    request.catch(function () {
      if (pageCache.get(cacheKey) === request) {
        pageCache.delete(cacheKey);
      }
    });

    return request;
  }

  function copyHeadValue(pageDocument, selector, attribute) {
    var source = pageDocument.querySelector(selector);
    var current = document.querySelector(selector);
    if (!source) return;

    if (!current) {
      document.head.appendChild(document.importNode(source, true));
      return;
    }

    if (source.hasAttribute(attribute)) {
      current.setAttribute(attribute, source.getAttribute(attribute));
    }
  }

  function updatePageMetadata(pageDocument) {
    if (pageDocument.title) {
      document.title = pageDocument.title;
    }

    copyHeadValue(pageDocument, 'link[rel="canonical"]', 'href');
    copyHeadValue(pageDocument, 'meta[name="description"]', 'content');
    copyHeadValue(pageDocument, 'meta[property="og:url"]', 'content');
    copyHeadValue(pageDocument, 'meta[property="og:title"]', 'content');
    copyHeadValue(pageDocument, 'meta[property="og:description"]', 'content');
    copyHeadValue(pageDocument, 'meta[property="og:image"]', 'content');
    copyHeadValue(pageDocument, 'meta[name="twitter:title"]', 'content');
    copyHeadValue(pageDocument, 'meta[name="twitter:description"]', 'content');
    copyHeadValue(pageDocument, 'meta[name="twitter:image"]', 'content');
  }

  function updateTemplateClasses(pageDocument) {
    var currentClasses = Array.prototype.slice.call(document.body.classList);
    currentClasses.forEach(function (className) {
      if (className === 'template-product' || className.indexOf('template-suffix-') === 0) {
        document.body.classList.remove(className);
      }
    });

    Array.prototype.slice.call(pageDocument.body.classList).forEach(function (className) {
      if (className === 'template-product' || className.indexOf('template-suffix-') === 0) {
        document.body.classList.add(className);
      }
    });

    if (window._template) {
      var suffixClass = Array.prototype.slice.call(pageDocument.body.classList).filter(function (className) {
        return className.indexOf('template-suffix-') === 0;
      })[0];
      window._template.directory = 'product';
      window._template.name = 'product';
      window._template.suffix = suffixClass ? suffixClass.replace('template-suffix-', '') : '';
    }
  }

  function updateProductGlobals(content) {
    var productJsonElement = content.querySelector('script[id^="ProductJson-"]');
    if (!productJsonElement) return;

    try {
      var productData = JSON.parse(productJsonElement.textContent);

      if (window.meta && typeof window.meta === 'object') {
        window.meta.product = productData;
      }

      if (window.ShopifyAnalytics && ShopifyAnalytics.meta) {
        ShopifyAnalytics.meta.product = productData;
      }

      if (window.KlarnaThemeGlobals) {
        var variants = productData.variants || [];
        var selectedVariant = variants.filter(function (variant) {
          return variant.available;
        })[0] || variants[0];

        window.KlarnaThemeGlobals.productVariants = variants;
        if (selectedVariant) {
          window.KlarnaThemeGlobals.data_purchase_amount = selectedVariant.price;
        }
      }
    } catch (error) {
      // The product form still works if an app has altered the JSON payload.
    }
  }

  function sectionId(section) {
    return (section.id || '').replace(/^shopify-section-/, '');
  }

  function dispatchSectionEvent(section, eventName) {
    section.dispatchEvent(new CustomEvent(eventName, {
      bubbles: true,
      detail: {
        sectionId: sectionId(section)
      }
    }));
  }

  function unloadContentSections(content) {
    var sections = Array.prototype.slice.call(content.querySelectorAll('.shopify-section'));
    sections.reverse().forEach(function (section) {
      dispatchSectionEvent(section, 'shopify:section:unload');
    });
  }

  function loadContentSections(content) {
    Array.prototype.slice.call(content.querySelectorAll('.shopify-section')).forEach(function (section) {
      dispatchSectionEvent(section, 'shopify:section:load');
    });

    window.requestAnimationFrame(function () {
      if (window.Shopify && Shopify.PaymentButton && typeof Shopify.PaymentButton.init === 'function') {
        Shopify.PaymentButton.init();
      }
      window.dispatchEvent(new Event('resize'));
    });
  }

  function historyPath(url) {
    return url.pathname + url.search + url.hash;
  }

  function updateHistory(url, shouldPush) {
    if (!shouldPush) return;
    if (window.location.href === url.href) return;

    window.history.pushState({ seasprayLinkedProduct: true }, '', historyPath(url));
  }

  function replaceProductContent(pageDocument, targetUrl, shouldPush) {
    var currentContent = document.querySelector(CONTENT_SELECTOR);
    var sourceContent = pageDocument.querySelector(CONTENT_SELECTOR);
    if (!currentContent || !sourceContent) {
      throw new Error('Product content could not be replaced');
    }

    var replacement = document.importNode(sourceContent, true);
    var hasSwapped = false;

    function swap() {
      unloadContentSections(currentContent);
      currentContent.replaceWith(replacement);
      hasSwapped = true;
      updatePageMetadata(pageDocument);
      updateTemplateClasses(pageDocument);
      updateProductGlobals(replacement);
      updateHistory(targetUrl, shouldPush);
      loadContentSections(replacement);
    }

    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (document.startViewTransition && !reduceMotion) {
      var transition;
      try {
        transition = document.startViewTransition(swap);
        return transition.updateCallbackDone.catch(function (error) {
          if (!hasSwapped) {
            swap();
          }
          return error;
        });
      } catch (error) {
        if (!hasSwapped) swap();
        return Promise.resolve();
      }
    }

    swap();
    return Promise.resolve();
  }

  function liveRegion() {
    var region = document.getElementById('LinkedProductNavigationStatus');
    if (region) return region;

    region = document.createElement('div');
    region.id = 'LinkedProductNavigationStatus';
    region.className = 'visually-hidden';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    document.body.appendChild(region);
    return region;
  }

  function announce(message) {
    var region = liveRegion();
    region.textContent = '';
    window.setTimeout(function () {
      region.textContent = message;
    }, 30);
  }

  function currentProductPath() {
    var section = document.querySelector(PRODUCT_SECTION_SELECTOR);
    if (!section) return '';
    var url = getProductUrl(section.getAttribute('data-product-url'));
    return url ? url.pathname : '';
  }

  function finishKeyboardFocus() {
    var heading = document.querySelector('.linked-colours__heading');
    if (!heading) return;
    heading.setAttribute('tabindex', '-1');
    heading.focus({ preventScroll: true });
    heading.addEventListener('blur', function () {
      heading.removeAttribute('tabindex');
    }, { once: true });
  }

  function fallbackNavigation(url, replaceHistory) {
    if (replaceHistory) {
      window.location.replace(url.href);
    } else {
      window.location.assign(url.href);
    }
  }

  function navigateToProduct(url, options) {
    var sequence = ++navigationSequence;
    var content = document.querySelector(CONTENT_SELECTOR);
    var trigger = options.trigger;
    var colourName = trigger && (trigger.getAttribute('title') || trigger.getAttribute('aria-label'));

    if (activeController) activeController.abort();
    activeController = typeof AbortController !== 'undefined' ? new AbortController() : null;

    if (content) {
      content.classList.add('linked-product-content--loading');
      content.setAttribute('aria-busy', 'true');
    }
    if (trigger) {
      trigger.setAttribute('aria-busy', 'true');
    }
    announce('Loading ' + (colourName || 'product'));

    fetchProductDocument(url, activeController ? activeController.signal : undefined).then(function (pageDocument) {
      if (sequence !== navigationSequence) return;
      return replaceProductContent(pageDocument, url, options.pushHistory).then(function () {
        if (sequence !== navigationSequence) return;
        var title = pageDocument.querySelector(PRODUCT_SECTION_SELECTOR + ' h1.title');
        announce((title ? title.textContent.trim() : colourName || 'Product') + ' loaded');
        if (options.focusAfterLoad) finishKeyboardFocus();
        document.dispatchEvent(new CustomEvent('seaspray:linked-product:load', {
          detail: {
            url: url.href
          }
        }));
      });
    }).catch(function (error) {
      if (error && error.name === 'AbortError') return;
      if (sequence !== navigationSequence) return;
      fallbackNavigation(url, options.replaceHistory);
    }).finally(function () {
      if (sequence !== navigationSequence) return;
      var activeContent = document.querySelector(CONTENT_SELECTOR);
      if (activeContent) {
        activeContent.classList.remove('linked-product-content--loading');
        activeContent.removeAttribute('aria-busy');
      }
      if (trigger && trigger.isConnected) {
        trigger.removeAttribute('aria-busy');
      }
      activeController = null;
    });
  }

  function linkedSwatchFromEvent(event) {
    var target = event.target;
    if (!target || !target.closest) return null;
    return target.closest(LINK_SELECTOR);
  }

  function prefetchFromEvent(event) {
    if (!window.fetch || !window.DOMParser || !window.Promise) return;
    var link = linkedSwatchFromEvent(event);
    if (!link) return;
    var url = getProductUrl(link.href);
    if (!url) return;
    fetchProductDocument(url).catch(function () {});
  }

  document.addEventListener('click', function (event) {
    var link = linkedSwatchFromEvent(event);
    if (!link || event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (link.target && link.target !== '_self') return;

    var url = getProductUrl(link.href);
    if (!url) return;
    if (!window.fetch || !window.DOMParser || !window.Promise) return;

    event.preventDefault();
    navigateToProduct(url, {
      trigger: link,
      pushHistory: true,
      replaceHistory: false,
      focusAfterLoad: event.detail === 0
    });
  });

  document.addEventListener('pointerover', prefetchFromEvent, { passive: true });
  document.addEventListener('focusin', prefetchFromEvent);
  document.addEventListener('touchstart', prefetchFromEvent, { passive: true });

  window.addEventListener('popstate', function () {
    if (window.location.pathname === currentProductPath()) return;

    var url = getProductUrl(window.location.href);
    if (!url) {
      window.location.reload();
      return;
    }

    navigateToProduct(url, {
      trigger: null,
      pushHistory: false,
      replaceHistory: true,
      focusAfterLoad: false
    });
  });
})();
